import {
  startAmbient, stopAmbient, ambientRunning, ambientScene, binauralBeat, updateBinauralBeat,
  startScene, stopScene, ambientSceneFile,
  startBowls, stopBowls, skipBowl, bowlsActive,
  startFeature, stopFeature, chimesActive, birdsongActive,
} from './ambientEngine';

const PRESETS = [
  { hz: 1.618,  label: '1.618 Hz δ' },
  { hz: 4.236,  label: '4.236 Hz θ' },
  { hz: 6.180,  label: '6.180 Hz α' },
  { hz: 16.180, label: '16.18 Hz β' },
  { hz: 61.803, label: '61.80 Hz γ' },
] as const;

const MIN_HZ = 0.5, MAX_HZ = 100;
const LOG_RANGE = Math.log(MAX_HZ / MIN_HZ);
const sliderToHz = (pos: number) => MIN_HZ * Math.exp((pos / 1000) * LOG_RANGE);
const hzToSlider = (hz: number) => Math.log(hz / MIN_HZ) / LOG_RANGE * 1000;
const band = (hz: number) => hz < 4 ? 'δ' : hz < 8 ? 'θ' : hz < 13 ? 'α' : hz < 30 ? 'β' : 'γ';

const SCENE_OPTIONS = [
  { id: '',                 label: 'no scene' },
  { id: 'scene-forest',    label: 'forest' },
  { id: 'scene-night',     label: 'night' },
  { id: 'scene-jungle',    label: 'jungle' },
  { id: 'scene-storm',     label: 'storm' },
  { id: 'scene-deluge',    label: 'deluge' },
  { id: 'scene-meadow',    label: 'meadow' },
  { id: 'scene-spring',    label: 'spring' },
  { id: 'scene-cuckoo',    label: 'cuckoo' },
  { id: 'scene-starlings', label: 'starlings' },
  { id: 'scene-ravens',    label: 'ravens' },
  { id: 'scene-sonora',    label: 'sonora' },
  { id: 'scene-zegenwerp', label: 'zegenwerp' },
];

const POSITIONS: Record<string, string> = {
  'bottom-left':  'bottom:18px;left:18px',
  'bottom-right': 'bottom:18px;right:18px',
  'top-left':     'top:18px;left:18px',
  'top-right':    'top:18px;right:18px',
};

function buildCSS(dark: boolean, pos: string): string {
  const posStyle = POSITIONS[pos] ?? POSITIONS['bottom-left'];
  const bg      = dark ? 'rgba(12,12,16,0.72)'    : 'rgba(255,255,255,0.72)';
  const border  = dark ? 'rgba(255,255,255,0.12)'  : 'rgba(0,0,0,0.10)';
  const text    = dark ? 'rgba(255,255,255,0.85)'  : 'rgba(0,0,0,0.80)';
  const muted   = dark ? 'rgba(255,255,255,0.45)'  : 'rgba(0,0,0,0.42)';
  const accent  = dark ? '#a78bfa' : '#7c3aed';
  const optBg   = dark ? '#1a1a2e' : '#fff';
  return `
:host{display:block;position:fixed;${posStyle};z-index:9000;font-size:14px !important;}
.wrap{font-family:Recursive,ui-sans-serif,system-ui,sans-serif;font-variation-settings:"wght" 440,"CASL" 0.2,"CRSV" 0,"MONO" 0;background:${bg};border:1px solid ${border};border-radius:14px;backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);box-shadow:0 8px 32px rgba(0,0,0,0.28);color:${text};overflow:hidden;}
.pill{display:flex;align-items:center;justify-content:center;width:40px;height:40px;cursor:pointer;background:none;border:none;color:${muted};font-size:1.1rem;transition:color .15s;border-radius:14px;}
.pill:hover,.pill.on{color:${accent};}
.panel{padding:10px 12px 12px;display:flex;flex-direction:column;gap:4px;min-width:160px;}
.hd{display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;}
.hd-label{font-size:.58rem;letter-spacing:.12em;opacity:.45;font-variation-settings:"MONO" 0.8;}
.close{background:none;border:none;cursor:pointer;color:${muted};font-size:.7rem;padding:2px 4px;border-radius:6px;}
.close:hover{color:${text};}
.row{display:flex;align-items:center;gap:8px;background:none;border:none;padding:3px 6px;margin:0 -6px;cursor:pointer;color:${muted};text-align:left;border-radius:999px;width:calc(100% + 12px);transition:color .15s;}
.row:hover{color:${text};}
.row.on{color:${accent};}
.ri{width:16px;flex-shrink:0;text-align:center;font-size:.78rem;line-height:1;}
.rl{font-size:.64rem;letter-spacing:.08em;font-variation-settings:"MONO" 0.8;}
.sel{appearance:none;-webkit-appearance:none;background:none;border:none;padding:0;cursor:pointer;font-family:inherit;font-size:.64rem;letter-spacing:.08em;font-variation-settings:"MONO" 0.8;color:inherit;border-radius:999px;}
.sel option{background:${optBg};color:${text};}
.skip{background:none;border:none;padding:0 2px;cursor:pointer;font-size:.85rem;color:${muted};transition:color .15s;line-height:1;}
.skip:hover{color:${accent};}
.beat{display:flex;align-items:center;gap:6px;padding-left:24px;margin-bottom:2px;}
.bl{background:none;border:none;padding:0;cursor:pointer;font-size:.58rem;letter-spacing:.06em;color:${muted};white-space:nowrap;font-variation-settings:"MONO" 0.8;font-family:inherit;}
.bl:hover{color:${accent};}
.bs{appearance:none;background:${border};border-radius:2px;height:2px;flex:1;cursor:pointer;}
.bs::-webkit-slider-thumb{appearance:none;background:${accent};border-radius:50%;height:10px;width:10px;}
.ftrs{display:flex;gap:10px;}
.ftrs .row{width:auto;}
`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function row(icon: string, label: string, on: boolean): HTMLButtonElement {
  const btn = el('button', `row${on ? ' on' : ''}`);
  const ri = el('span', 'ri', icon);
  const rl = el('span', 'rl', label);
  btn.appendChild(ri);
  btn.appendChild(rl);
  return btn;
}

class AmbientWidget extends HTMLElement {
  private shadow: ShadowRoot;
  private _unsubs: Array<() => void> = [];
  private _open = false;

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['position', 'theme']; }

  connectedCallback() {
    this._render();
    this._unsubs.push(
      ambientRunning.subscribe(() => this._patch()),
      ambientScene.subscribe(() => this._patch()),
      binauralBeat.subscribe(() => this._patch()),
      ambientSceneFile.subscribe(() => this._patch()),
      bowlsActive.subscribe(() => this._patch()),
      chimesActive.subscribe(() => this._patch()),
      birdsongActive.subscribe(() => this._patch()),
    );
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if ((this.getAttribute('theme') ?? 'auto') === 'auto') this._render();
    });
  }

  disconnectedCallback() {
    this._unsubs.forEach(fn => fn());
    stopAmbient(); stopScene(); stopBowls();
  }

  attributeChangedCallback() { this._render(); }

  private _isDark() {
    const t = this.getAttribute('theme') ?? 'auto';
    return t === 'auto' ? window.matchMedia('(prefers-color-scheme: dark)').matches : t === 'dark';
  }

  private _render() {
    const pos   = this.getAttribute('position') ?? 'bottom-left';
    const dark  = this._isDark();
    this.shadow.replaceChildren();

    const style = el('style');
    style.textContent = buildCSS(dark, pos);
    this.shadow.appendChild(style);

    const wrap = el('div', 'wrap');

    if (!this._open) {
      const anySoundOn = ambientRunning.value || !!ambientSceneFile.value
        || bowlsActive.value || chimesActive.value || birdsongActive.value;
      const pill = el('button', `pill${anySoundOn ? ' on' : ''}`, '♩');
      pill.title = 'ambient';
      pill.addEventListener('click', () => { this._open = true; this._render(); });
      wrap.appendChild(pill);
    } else {
      wrap.appendChild(this._buildPanel());
    }

    this.shadow.appendChild(wrap);
  }

  private _buildPanel(): HTMLElement {
    const run    = ambientRunning.value;
    const scene  = ambientScene.value;
    const hz     = binauralBeat.value;
    const sfile  = ambientSceneFile.value;
    const bowls  = bowlsActive.value;
    const chimes = chimesActive.value;
    const birds  = birdsongActive.value;

    const panel = el('div', 'panel');

    // header
    const hd = el('div', 'hd');
    hd.appendChild(el('span', 'hd-label', 'ambient'));
    const closeBtn = el('button', 'close', '×');
    closeBtn.addEventListener('click', () => { this._open = false; this._render(); });
    hd.appendChild(closeBtn);
    panel.appendChild(hd);

    // tones
    const tonesBtn = row('♩', 'tones', run);
    tonesBtn.addEventListener('click', () => {
      if (run) stopAmbient(); else startAmbient(scene, 0.75);
    });
    panel.appendChild(tonesBtn);

    // binaural sub-row
    if (run && scene === 'binaural') {
      const preset = PRESETS.find(p => Math.abs(p.hz - hz) < 0.01);
      const label  = preset?.label ?? `${band(hz)} · ${hz < 10 ? hz.toFixed(1) : Math.round(hz)} Hz`;
      const beat = el('div', 'beat');
      const bl = el('button', 'bl', label);
      bl.addEventListener('click', () => {
        const idx  = PRESETS.findIndex(p => Math.abs(p.hz - hz) < 0.01);
        const next = PRESETS[(idx + 1) % PRESETS.length];
        run && scene === 'binaural' ? updateBinauralBeat(next.hz) : binauralBeat.set(next.hz);
      });
      const slider = el('input', 'bs');
      slider.type = 'range'; slider.min = '0'; slider.max = '1000'; slider.step = '1';
      slider.value = String(Math.round(hzToSlider(hz)));
      slider.addEventListener('input', e => {
        const v = Math.round(sliderToHz(Number((e.target as HTMLInputElement).value)) * 10) / 10;
        run && scene === 'binaural' ? updateBinauralBeat(v) : binauralBeat.set(v);
      });
      beat.appendChild(bl); beat.appendChild(slider);
      panel.appendChild(beat);
    }

    // scene (real audio)
    const sceneDiv = el('div', `row${sfile ? ' on' : ''}`);
    sceneDiv.appendChild(el('span', 'ri', '∿'));
    const sel = el('select', 'sel');
    SCENE_OPTIONS.forEach(opt => {
      const o = el('option');
      o.value = opt.id; o.textContent = opt.label;
      if (opt.id === (sfile ?? '')) o.selected = true;
      sel.appendChild(o);
    });
    sel.addEventListener('change', e => {
      const v = (e.target as HTMLSelectElement).value;
      v ? startScene(v) : stopScene();
    });
    const shuffleBtn = el('button', 'skip', '⇄');
    shuffleBtn.title = 'random scene';
    shuffleBtn.addEventListener('click', () => {
      const opts = SCENE_OPTIONS.filter(o => o.id !== '');
      startScene(opts[Math.floor(Math.random() * opts.length)].id);
    });
    sceneDiv.appendChild(sel); sceneDiv.appendChild(shuffleBtn);
    panel.appendChild(sceneDiv);

    // bowls
    const bowlsDiv = el('div', `row${bowls ? ' on' : ''}`);
    const bowlBtn = el('button');
    bowlBtn.style.cssText = 'display:flex;align-items:center;gap:8px;background:none;border:none;padding:0;cursor:pointer;color:inherit;';
    bowlBtn.appendChild(el('span', 'ri', '◯'));
    bowlBtn.appendChild(el('span', 'rl', 'bowls'));
    bowlBtn.addEventListener('click', () => bowls ? stopBowls() : startBowls());
    bowlsDiv.appendChild(bowlBtn);
    if (bowls) {
      const skipBtn = el('button', 'skip', '›');
      skipBtn.title = 'next bowl';
      skipBtn.addEventListener('click', skipBowl);
      bowlsDiv.appendChild(skipBtn);
    }
    panel.appendChild(bowlsDiv);

    // features
    const ftrs = el('div', 'ftrs');
    const chimesBtn = row('⌁', 'chimes', chimes);
    chimesBtn.addEventListener('click', () => chimes ? stopFeature('chimes') : startFeature('chimes'));
    const birdsBtn = row('♭', 'birds', birds);
    birdsBtn.addEventListener('click', () => birds ? stopFeature('birdsong') : startFeature('birdsong'));
    ftrs.appendChild(chimesBtn); ftrs.appendChild(birdsBtn);
    panel.appendChild(ftrs);

    return panel;
  }

  private _patch() { if (this._open) this._render(); }
}

if (!customElements.get('ambient-widget')) {
  customElements.define('ambient-widget', AmbientWidget);
}
